<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
    <div class="chart-control-buttons">
    <button class="custom-button" @click="toggleScale">{{ logScaleText }}</button>
    <div class="slider-container">
        <input 
          type="range" 
          min="0" 
          max="100" 
          v-model="sliderValue" 
          @input="updateSliderValue" 
          class="custom-slider"
        >
        <span>{{ sliderValue }}x</span>
      </div>
    <button class="custom-button" @click="resetZoom">Reset Zoom</button>
  </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(...registerables, zoomPlugin);

export default {
  name: 'CustomChart',
  // data() {
  //   return {
  //     chartInstance: null,

  //   };
  // },
  methods: {
  },
  props: {
    chartTitle: {
      type: String,
      required: true,
    },
    chartType: {
      type: String,
      required: true,
    },
    dataUnit: {
      type: String,
      required: true,
    },
    datasets: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every(dataset => 
          'name' in dataset && 
          'values' in dataset && 
          'color' in dataset &&
          'labels' in dataset
        );
      },
    },
    yAxisMin: {
      type: Number,
      required: false,
      default: null,
    },
    yAxisMax: {
      type: Number,
      required: false,
      default: null,
    },
    hiddenLegends: {
      type: Array,
      required: false,
      default: () => [],
  },
  isFilled: {  // Determining if the area should be filled
      type: Boolean,
      default: false,
    },
  },


  setup(props, { emit }) {
  let localHiddenLegends = [];
  const chartCanvas = ref(null);
  let chartInstance = null;
  const logScale = ref(false);
  const sliderValue = ref(0);

  const logScaleText = computed(() => {
    return logScale.value ? 'Linear Scale' : 'Log Scale';
  });

  const createChart = () => {
    try {
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
      
      if (!chartCanvas.value) {
        // console.warn('Chart canvas ref not available, skipping chart creation');
        return;
      }

      localHiddenLegends = props.hiddenLegends || [];
      const uniqueLocalHiddenLegends = [...new Set(localHiddenLegends)];
      localHiddenLegends = uniqueLocalHiddenLegends;

      // Detect dark mode
      const isDarkMode = document.documentElement.classList.contains('dark-mode');
      const backgroundColor = isDarkMode ? '#2a2a2a' : '#ffffff';
      const textColor = isDarkMode ? '#e0e0e0' : '#333333';
      const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
      const tickColor = isDarkMode ? '#e0e0e0' : '#666666';

         
  const datasets = Array.isArray(props.datasets) ? props.datasets : [];


  chartInstance = new Chart(chartCanvas.value, {
    type: props.chartType,
    data: {
      labels: datasets.length > 0 ? datasets[0].labels : [],
      datasets: datasets.map(dataset => ({
        label: dataset.name,
        data: dataset.values,
        borderColor: dataset.color,
        // backgroundColor: dataset.group == "soc_actual" ? dataset.color: 'rgba(0, 0, 0, 0)',
        backgroundColor: dataset.color,
        borderDash: (() => {
  const lastPart = dataset.group.split('_')[dataset.group.split('_').length - 1];
  if (lastPart === 'actual') {
    return [0, 0]; // For 'actual'
  } else if (lastPart === 'adjusted') {
    return [7, 7]; // For 'adjusted'
  } else {
    return [2, 2]; // Default case for others
  }
})(),

        fill: props.isFilled,
        hidden: localHiddenLegends.length > 0 && localHiddenLegends.includes(dataset.name),

        // Store the original group for filtering
        originalGroup: dataset.group,
        
        // Handle warning datasets styling
        showLine: dataset.group !== 'price_actual_warnings',
        pointRadius: dataset.group === 'price_actual_warnings' ? 4 : 0,
        pointHoverRadius: dataset.group === 'price_actual_warnings' ? 8 : 7,
      })),
    },


      options: {
        responsive: true,
        backgroundColor: backgroundColor,
        elements: {
            point: {
              radius: 0, // Hide dots on the line
              hitRadius: 15, // Hit area size (optional)
              backgroundColor: backgroundColor, // Color inside the point
            },
            line: {
              borderWidth: isMobile() ? 1 : 2.5,  // Smaller width for mobile
            },
          },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: props.chartTitle,
            color: textColor,
          },
          legend: {
                display: true,
                position: 'top',
                labels: {
                  color: textColor,
                  filter: function (legendItem, chartData) {
                    const dataset = chartData.datasets[legendItem.datasetIndex];

                    return !(
                      dataset.label?.includes('Unreliable') ||
                      dataset.originalGroup?.includes('warnings')
                    );
                  }
                },
                onClick: (e, legendItem) => {
                  const index = legendItem.datasetIndex;
                  const ci = chartInstance;
                  const meta = ci.getDatasetMeta(index);

                  // Toggle the visibility of the clicked dataset
                  meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;

                  const itemName = ci.data.datasets[index].label;

                  // Update localHiddenLegends based on visibility
                  if (meta.hidden) {
                    // If the dataset is hidden, add to localHiddenLegends if not already present
                    if (!localHiddenLegends.includes(itemName)) {
                      localHiddenLegends.push(itemName);
                    }
                  } else {
                    // If the dataset is visible, remove from localHiddenLegends if present
                    const itemIndex = localHiddenLegends.indexOf(itemName);
                    if (itemIndex > -1) {
                      localHiddenLegends.splice(itemIndex, 1);
                    }
                  }

                  // Update the chart
                  ci.update();

                  // Determine hidden datasets and their labels
                  const hiddenLegends = ci.data.datasets
                    .filter((dataset, i) => ci.getDatasetMeta(i).hidden);

                  // Extract labels from the hiddenLegends
                  const hiddenLabels = hiddenLegends.map(dataset => dataset.label);

                  // Ensure labels are unique
                  const filteredHiddenLabels = [...new Set(hiddenLabels)];

                  // console.log('hiddenLabels: ', hiddenLabels);
                  // console.log('filteredHiddenLabels: ', filteredHiddenLabels);
                  // console.log('localHiddenLegends: ', localHiddenLegends);

                  // Combine localHiddenLegends with hiddenLabels and filter out null values
                  const combinedLabelsList = localHiddenLegends.concat(hiddenLabels);
                  const cleanedList = combinedLabelsList.filter(item => item != null);

                  // console.log('cleanedList: ', cleanedList);

                  // Emit the hidden legends to the parent component
                  emit('legends-updated', cleanedList);
                },
              },


          tooltip: {
            callbacks: {
              label: function(tooltipItem) {
                return `${tooltipItem.dataset.label}: ${tooltipItem.raw} ${props.dataUnit}`;
              }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              onPan: () => {
                const { min, max } = chartInstance.scales['x'];
                // console.log('sliderValue.value: ', sliderValue.value);
                // console.log('min: ', min);
                // console.log('max: ', max);
                emit('pan-updated', { min, max, sliderValue: sliderValue.value });
              }
            },
            zoom: {
              wheel: {
                enabled: false,
              },
              pinch: {
                enabled: true,
              },
              mode: 'x',
              onZoom: () => {
                const { min, max } = chartInstance.scales['x'];
                  // console.log('sliderValue.value: ', sliderValue.value);
                  // console.log('newMin: ', newMin);
                  // console.log('newMax: ', newMax);
                emit('zoom-updated', { min, max, sliderValue: sliderValue.value });
              }
            },
          },
          
          unitDisplay: {
            id: 'unitDisplay',
            afterDraw: (chart) => {
              const { ctx, chartArea } = chart;
              const isDarkMode = document.documentElement.classList.contains('dark-mode');
              const textColor = isDarkMode ? '#e0e0e0' : '#333333';
              ctx.save();
              ctx.font = 'bold 12px Arial';
              ctx.fillStyle = textColor;
              ctx.textAlign = 'left';
              ctx.textBaseline = 'middle';
              ctx.fillText(props.dataUnit, chartArea.left, chartArea.top - 10);
              ctx.restore();
            },
          },
        },
        scales: {
          x: {
            ticks: {
              maxTicksLimit: 10, // Limit the number of ticks shown
              autoSkip: true, // Automatically skip ticks to fit
              maxRotation: 45, // Rotate labels if needed
              color: tickColor,
            },
            grid: {
              display: true,
              color: gridColor,
            },
            title: {
              display: true,
              color: textColor,
            }
          },
          y: {
            type: logScale.value ? 'logarithmic' : 'linear',
            min: props.yAxisMin,
            max: props.yAxisMax,
            ticks: {
              color: tickColor,
              callback: function(value) {
                if (value % 1 === 0) return value;
                return null;
              },
            },
            grid: {
              color: gridColor,
            },
            title: {
              display: true,
              text: props.dataUnit,
              color: textColor,
            }
          },
        },
      },
    });
    } catch (error) {
      console.error('Error creating chart:', error);
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
    }
  };

  const updateChart = ({ min, max, sliderValue: newSliderValue }) => {
    try {
      // console.log('Updating charts with:', { min, max, newSliderValue });
      if (chartInstance && chartInstance.scales) {
        const xScale = chartInstance.scales['x'];
        if (xScale) {      
          xScale.options.min = min;
          xScale.options.max = max; 
          chartInstance.update();
          sliderValue.value = newSliderValue;
        }
      }
    } catch (error) {
      console.error('Error updating chart:', error);
    }
  };
  let initialMin = null;
  let initialMax = null;
  const updateSliderValue = () => {
    try {
      if (chartInstance && chartInstance.scales) {
        // Exponential zoom factor for deeper zoom
        const zoomFactor = Math.exp(sliderValue.value / 30); // Adjust the denominator to control zoom intensity
        const xScale = chartInstance.scales['x'];

        if (xScale) {
          if (initialMin === null && initialMax === null) {
            initialMin = xScale.min;
            initialMax = xScale.max;
          }

          // Calculate the new min and max values
          const centerPoint = (initialMax + initialMin) / 2;
          const newMin = centerPoint - ((centerPoint - initialMin) / zoomFactor);
          const newMax = centerPoint + ((initialMax - centerPoint) / zoomFactor);

          // Update the scale with the new min and max values
          xScale.options.min = newMin;
          xScale.options.max = newMax;

          chartInstance.update();

          // Emit the updated zoom values and slider value to the parent component
          // console.log('sliderValue.value inside updateSliderValue: ', sliderValue.value);
          // console.log('newMin inside updateSliderValue: ', newMin);
          // console.log('newMax inside updateSliderValue: ', newMax);
          emit('zoom-updated', { min: newMin, max: newMax, sliderValue: sliderValue.value });
        }
      }
    } catch (error) {
      console.error('Error updating slider value:', error);
    }
  };

// Function to determine if the screen width is mobile
const isMobile = () => {
  return window.innerWidth <= 750;
}


  const toggleScale = () => {
    try {
      logScale.value = !logScale.value;
      if (chartInstance && chartInstance.options && chartInstance.options.scales) {
        chartInstance.options.scales.y.type = logScale.value ? 'logarithmic' : 'linear';
        chartInstance.update();
      }
    } catch (error) {
      console.error('Error toggling scale:', error);
    }
  };

  const resetZoom = () => {
    try {
      if (chartInstance && chartInstance.scales) {
        const xScale = chartInstance.scales['x'];

        if (xScale) {
          // Reset the min and max to the original values
          xScale.options.min = initialMin;
          xScale.options.max = initialMax;

          // Reset the slider value to 0
          sliderValue.value = 0;

          chartInstance.update();

          // console.log('sliderValue.value: ', sliderValue.value);
          // console.log('initialMin: ', initialMin);
          // console.log('initialMax: ', initialMax);
          // Emit the reset zoom values and slider value to the parent component
          emit('zoom-updated', { min: initialMin, max: initialMax, sliderValue: sliderValue.value });
        }
      }
    } catch (error) {
      console.error('Error resetting zoom:', error);
    }
  };


  onMounted(() => {
    try {
      createChart();
    } catch (error) {
      console.error('Error in onMounted:', error);
    }
  });

  onBeforeUnmount(() => {
    try {
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
    } catch (error) {
      console.error('Error cleaning up chart:', error);
    }
  });

  watch(
    () => [props.chartTitle, props.chartType, props.datasets, props.yAxisMin, props.yAxisMax],
    (newValue, oldValue) => {
      try {
        if (newValue !== oldValue) {
          createChart();
        }
      } catch (error) {
        console.error('Error in watch callback:', error);
      }
    },
    { deep: true }
  );


  
  return {
    chartCanvas,
    sliderValue, 
    toggleScale,
    logScaleText,
    updateSliderValue,
    resetZoom,
    localHiddenLegends,
    updateChart,
  };
}

};
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 500px;
}


canvas {
  width: 100%;
  height: 500px;
}
.frame {
  height: 25rem;
}


.custom-button {
  margin-top: 10px;
  margin-right: 30px;
  margin-left: 20px;
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: none;
  border-radius: 5px;
  padding: 4px 7px;
  font-size: 14px;
}

.custom-button:hover {
  box-shadow: 0 2px 6px rgba(60,64,67,0.25);
  background: var(--custom-color1);
  color: white;
}

.slider-container {
  display: inline-flex;
  align-items: center;
}
.custom-slider {
  -webkit-appearance: none;
  width: 200px;
  height: 10px;
  border-radius: 5px;
  background: var(--bg-tertiary);
  outline: none;
  opacity: 0.7;
  transition: opacity .2s;
  margin-right: 10px;
}
.custom-slider:hover {
  opacity: 1;
}
.custom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--custom-color1);
  cursor: pointer;
}
.custom-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--custom-color1);
  cursor: pointer;
}


@media (max-width: 750px) {

  h1, h2, h3, h4, h5, h6, p, label, input, select, button, option, th, td {
    font-size: 70%;
  }

  .chart-control-buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 5%;
  }

  .custom-button {
    font-size: 0.4rem;
  }

  .chart-container {
    position: relative;
    width: 100%;
    height: 700px;
  }

  canvas {
    width: 100%;
    height: 700px;
  }
  .frame {
    height: 700px;
  }

}

</style>


